/**
 * BootReadyEffects — things that happen once Central's switch-context is
 * applied, mounted by BootGate on its ready branch. Renders nothing.
 *
 * 1. Return from Central billing (suite contract 1.2, BH-07)
 *    `?billing=success|cancel` → re-read switch-context in the background,
 *    invalidate the entitlement-derived reads, announce a credit-balance
 *    re-read (announceCentralBillingRefresh), say what happened, strip the
 *    param. Also on window focus / visibility / bfcache restore after a billing
 *    page was opened from this tab (the flag set by openCentralBilling).
 *
 * 2. Workspace hint on arrival (suite contract 1.1)
 *    `?workspaceId=<id>` that differs from the token's workspace → switch via
 *    Central's select-workspace (useWorkspace — classified), at most once per
 *    tab per workspace; the param is always stripped.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AppDispatch } from '../../store';
import { selectCentralAccessToken, selectTokenWorkspaceId } from '../../store/slices/centralAuthSlice';
import { refreshSwitchContext } from '../../auth/refreshSwitchContext';
import {
  announceCentralBillingRefresh,
  consumeCentralBillingOpened,
  readBillingReturn,
} from '../../services/centralBillingUrls';
import { useWorkspace } from '../../hooks/useWorkspace';

/**
 * react-hot-toast is loaded on first use, not at import: its stylesheet setup
 * touches `document` at module evaluation, and BootGate (which mounts this) is
 * imported by DOM-less test suites.
 */
function notify(kind: 'success' | 'error' | 'info', message: string, id: string): void {
  void import('react-hot-toast').then(({ default: toast }) => {
    if (kind === 'success') toast.success(message, { id });
    else if (kind === 'error') toast.error(message, { id });
    else toast(message, { id });
  });
}

const WORKSPACE_HINT_KEY_PREFIX = 'beheard_workspace_hint_handled:';
const WORKSPACE_ID_SHAPE = /^[A-Za-z0-9-]{8,64}$/;

/** `search` without the named params (keeps every other param, in order). */
export function stripSearchParams(search: string, names: readonly string[]): string {
  const params = new URLSearchParams(search);
  for (const n of names) params.delete(n);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export interface WorkspaceHintAction {
  /** The param was present and must be removed from the URL. */
  strip: boolean;
  /** Switch to this workspace, or null. */
  switchTo: string | null;
}

/**
 * What a `?workspaceId=` arrival asks for. Pure.
 *   absent                       → nothing
 *   malformed                    → strip only
 *   same as the token workspace  → strip only
 *   already handled in this tab  → strip only (at most once per tab)
 *   otherwise                    → strip + switch
 */
export function workspaceHintAction(
  search: string,
  tokenWorkspaceId: string | null,
  alreadyHandled: (workspaceId: string) => boolean,
): WorkspaceHintAction {
  const hinted = new URLSearchParams(search).get('workspaceId');
  if (hinted === null) return { strip: false, switchTo: null };
  if (!WORKSPACE_ID_SHAPE.test(hinted)) return { strip: true, switchTo: null };
  // Workspace ids are compared case-insensitively (contract 1.1): a hint that
  // names the token's own workspace in another case is "already there".
  if (tokenWorkspaceId !== null && hinted.toLowerCase() === tokenWorkspaceId.toLowerCase()) {
    return { strip: true, switchTo: null };
  }
  if (alreadyHandled(hinted.toLowerCase())) return { strip: true, switchTo: null };
  return { strip: true, switchTo: hinted };
}

function hintHandled(workspaceId: string): boolean {
  try {
    return sessionStorage.getItem(WORKSPACE_HINT_KEY_PREFIX + workspaceId) !== null;
  } catch {
    return false;
  }
}

function markHintHandled(workspaceId: string): void {
  try {
    sessionStorage.setItem(WORKSPACE_HINT_KEY_PREFIX + workspaceId, '1');
  } catch {
    /* storage unavailable: the param is stripped, so it cannot repeat anyway */
  }
}

export function BootReadyEffects() {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const token = useSelector(selectCentralAccessToken);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  const refresh = useCallback(
    () => refreshSwitchContext({ dispatch, getToken: () => tokenRef.current }),
    [dispatch],
  );

  const replaceSearch = useReplaceSearch();
  useWorkspaceHint(replaceSearch);

  // 1a. ?billing=success|cancel
  useEffect(() => {
    const result = readBillingReturn(location.search);
    if (!result) return;
    consumeCentralBillingOpened();
    void refresh();
    // Every screen showing Central's credit balance re-reads it.
    announceCentralBillingRefresh();
    if (result === 'success') {
      notify('success', 'Billing updated in LevelUp. Refreshing your plan…', 'billing-return');
    } else {
      notify('info', 'Checkout cancelled. Nothing was changed.', 'billing-return');
    }
    replaceSearch(['billing']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // 1b. Back from a billing page opened from this tab, without a ?billing= param.
  useEffect(() => {
    const onReturn = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (consumeCentralBillingOpened()) {
        void refresh();
        announceCentralBillingRefresh();
      }
    };
    window.addEventListener('focus', onReturn);
    window.addEventListener('pageshow', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      window.removeEventListener('pageshow', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [refresh]);

  return null;
}

/**
 * The same-origin location to replace to after removing `names` from the query.
 * A pathname that begins with more than one slash (or a backslash) — e.g. the
 * page was loaded as `https://app//x` — is collapsed to ONE leading slash:
 * re-emitting "//x" would be a protocol-relative URL, which `replaceState`
 * rejects (throws) and which would otherwise name another host.
 */
export function safeCleanupTarget(
  pathname: string,
  search: string,
  hash: string,
  names: readonly string[],
): { pathname: string; search: string; hash: string } {
  const safePath = /^[/\\]{2,}|^\/\\/.test(pathname) || pathname[0] !== '/'
    ? `/${pathname.replace(/^[/\\]+/, '')}`
    : pathname;
  return { pathname: safePath, search: stripSearchParams(search, names), hash };
}

/** Replace the URL's query without `names`, reading the CURRENT query so two strips in one commit compose. */
function useReplaceSearch(): (names: readonly string[]) => void {
  const location = useLocation();
  const navigate = useNavigate();
  return useCallback(
    (names: readonly string[]) => {
      const live =
        typeof window !== 'undefined' && window.location?.pathname === location.pathname
          ? window.location.search
          : location.search;
      // Never throws, never leaves the origin: a failed clean-up only leaves the
      // param in the address bar, which is harmless (the hint is once-per-tab).
      try {
        navigate(safeCleanupTarget(location.pathname, live, location.hash, names), { replace: true });
      } catch (err) {
        console.warn('[BootReadyEffects] could not clean the URL:', err);
      }
    },
    [navigate, location.pathname, location.search, location.hash],
  );
}

export interface ApplyWorkspaceHintDeps {
  search: string;
  tokenWorkspaceId: string | null;
  alreadyHandled: (workspaceId: string) => boolean;
  markHandled: (workspaceId: string) => void;
  stripParam: () => void;
  switchTo: (workspaceId: string) => Promise<{ success: boolean; error?: string }>;
  onError: (message: string) => void;
}

/**
 * The body of the `?workspaceId=` effect (contract 1.1), callable on its own.
 * Strips the param; when the hint names another workspace not yet handled in
 * this tab, records it FIRST (at most once per tab, even if the switch fails)
 * and re-authorizes to it through Central's select-workspace (useWorkspace).
 */
export async function applyWorkspaceHint(deps: ApplyWorkspaceHintDeps): Promise<void> {
  const action = workspaceHintAction(deps.search, deps.tokenWorkspaceId, deps.alreadyHandled);
  if (!action.strip) return;
  deps.stripParam();
  if (!action.switchTo) return;
  // Recorded lower-cased: "once per tab" holds whatever case the hint arrives in.
  deps.markHandled(action.switchTo.toLowerCase());
  const result = await deps.switchTo(action.switchTo);
  if (!result.success) deps.onError(result.error || "Couldn't open that workspace.");
}

/**
 * `?workspaceId=` on arrival. Runs on BootGate's READY branch (via
 * BootReadyEffects) AND on its REFUSAL branch (via WorkspaceHintEffect): when
 * the token's own workspace is refused at boot, the hint is exactly what can
 * get the user somewhere useful (QA-B P2 — it used to be ignored there).
 */
function useWorkspaceHint(replaceSearch?: (names: readonly string[]) => void): void {
  const location = useLocation();
  const ownReplace = useReplaceSearch();
  const strip = replaceSearch ?? ownReplace;
  const tokenWorkspaceId = useSelector(selectTokenWorkspaceId);
  const { switchWorkspace } = useWorkspace();

  useEffect(() => {
    void applyWorkspaceHint({
      search: location.search,
      tokenWorkspaceId,
      alreadyHandled: hintHandled,
      markHandled: markHintHandled,
      stripParam: () => strip(['workspaceId']),
      switchTo: switchWorkspace,
      onError: (message) => notify('error', message, 'workspace-hint'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
}

/** The hint handler alone, for BootGate's refusal branch. Renders nothing. */
export function WorkspaceHintEffect() {
  useWorkspaceHint();
  return null;
}
